import { doc, getDoc } from 'firebase/firestore';
import { matchIsValidTel } from 'mui-tel-input';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import uniqid from 'uniqid';
import { changeDeliveryInfo } from '../../../api/order/changeDeliveryInfo';
import { createOrder } from '../../../api/order/createOrder';
import { ClientCafe, Order } from '../../../apiTypes';
import EditModal from '../../../components/EditModal';
import FormTelField from '../../../components/forms/FormTelField';
import FormTextField from '../../../components/forms/FormTextField';
import { t } from '../../../constants/translations';
import { useAdmin } from '../../../contexts/admin.context';
import { useCafe } from '../../../contexts/cafe.context';
import { useLocale } from '../../../contexts/locale.context';
import { db } from '../../../firebase';
import { setDoc } from '../../../utils/api';
import getDiscount from '../../../utils/getDiscount';

type Props = {
  onClose: () => void;
  order?: DbDocument<Order>;
};

type FormType = NonNullable<Order['delivery']>;

const EditDeliveryOrderModal = (props: Props) => {
  const { order, onClose } = props;
  const { lang } = useLocale();
  const { cafe, cafeSlug } = useAdmin();
  const navigate = useNavigate();
  const { worker } = useCafe();

  const [loading, setLoading] = useState(false);
  const [client, setClient] = useState<ClientCafe | null>(null);

  const form = useForm<FormType>({
    mode: 'onChange',
    defaultValues: {
      name: order?.delivery?.name || '',
      address: order?.delivery?.address || '',
      phoneNumber: order?.delivery?.phoneNumber || '',
      comment: order?.delivery?.comment || '',
    },
  });

  const { watch, setValue } = form;
  const phoneNumber = watch('phoneNumber');
  const isValidTel = useMemo(() => matchIsValidTel(phoneNumber), [phoneNumber]);

  const handleSubmit = useCallback(
    async (delivery: FormType) => {
      if (worker && cafe) {
        setLoading(true);

        if (!order?.id && !client) {
          await setDoc<ClientCafe>(`cafes/${cafeSlug}/clients/${delivery.phoneNumber}`, {
            added: Date.now(),
            type: 'individual',
            name: delivery.name,
            phoneNumber: delivery.phoneNumber,
            address: delivery.address,
            birthday: 0,
            email: '',
            discountType: 'auto',
            personalDiscount: 0,
            totalSum: 0,
          });
        }

        if (order) {
          await changeDeliveryInfo({
            cafeSlug,
            order,
            worker,
            newDelivery: delivery,
          });
          onClose();
          setLoading(false);
        } else {
          const id = uniqid();
          await createOrder({
            id,
            workerId: worker.id,
            workerName: worker.name,
            cafe,
            cafeSlug,
            tableId: '',
            tableName: '',
            clientId: client?.phoneNumber || delivery.phoneNumber,
            clientName: client?.name || delivery.name,
            discount: getDiscount(client, cafe.loyalty),
            delivery,
            status: 'processing',
            type: 'delivery',
          });
          onClose();
          setLoading(false);
          navigate(`/${cafeSlug}/pos/delivery/?order=${id}`);
        }
      }
    },
    [cafe, cafeSlug, client, navigate, onClose, order, worker]
  );

  useEffect(() => {
    if (!order?.id && isValidTel && cafe?.id && phoneNumber) {
      const getClient = async () => {
        setLoading(true);
        const clientCafeDocSnap = await getDoc(doc(db, 'cafes', cafe.id, 'clients', phoneNumber));
        if (clientCafeDocSnap.exists()) {
          const client = clientCafeDocSnap.data() as ClientCafe;
          setValue('name', client.name);
          setValue('address', client.address);
          setClient(client);
        } else {
          setClient(null);
        }
        setLoading(false);
      };
      getClient();
    }
  }, [cafe?.id, isValidTel, order?.id, phoneNumber, setValue]);

  return (
    <EditModal<FormType>
      title={t.delivery_order[lang]}
      open
      onClose={onClose}
      submitButton={order ? t.save_changes[lang] : t.create_order[lang]}
      onSubmit={handleSubmit}
      submitButtonProps={{ disabled: loading }}
      form={form}
      size="xs"
    >
      <FormTelField
        fullWidth
        label={t.phone[lang]}
        color="secondary"
        required
        name="phoneNumber"
        sx={{ mb: 2 }}
        InputProps={{
          sx: { bgcolor: (t) => t.palette.background.paper },
        }}
      />

      <FormTextField fullWidth label={t.userName[lang]} name="name" sx={{ mb: 3, mt: 1 }} color="secondary" />

      <FormTextField name="address" fullWidth label={t.address[lang]} sx={{ mb: 3 }} color="secondary" />

      <FormTextField name="comment" fullWidth label={t.comment[lang]} color="secondary" />
    </EditModal>
  );
};

export default EditDeliveryOrderModal;
